import { Download, ImageOff, Paperclip } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type {
  CartLineCustomization,
  CartLineCustomizationFile,
  OrderLine,
} from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { useStore } from '@/shared/store/store-context';

import { useDownloadUpload } from '../hooks/use-download-upload';
import { formatBytes } from '../schemas/format-bytes';

function LineThumb({ url, title }: { url: string | null; title: string }) {
  if (!url) {
    return (
      <div className="bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded-md border">
        <ImageOff className="size-4" aria-hidden />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={title}
      className="size-10 shrink-0 rounded-md border object-cover"
      loading="lazy"
    />
  );
}

/**
 * A shopper upload: the filename and size, and a download that goes through the API client so
 * the session travels with it. The upload id is never shown; it is only the download target.
 */
function FileCustomizationChip({
  customization,
  file,
}: {
  customization: CartLineCustomization;
  file: CartLineCustomizationFile;
}) {
  const { t } = useTranslation('orders');
  const store = useStore();
  const message = useErrorMessage();
  const download = useDownloadUpload(store.id);
  return (
    <Badge variant="secondary" className="gap-1.5 pr-1 font-normal">
      <Paperclip className="size-3" aria-hidden />
      <span>
        {customization.label}: {file.filename}
        <span className="text-muted-foreground"> · {formatBytes(file.bytes)}</span>
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label={t('lines.download', { filename: file.filename })}
        disabled={download.isPending}
        onClick={() => download.mutate(file, { onError: (error) => toast.error(message(error)) })}
      >
        <Download aria-hidden />
      </Button>
    </Badge>
  );
}

function CustomizationChip({ customization }: { customization: CartLineCustomization }) {
  const { t } = useTranslation('orders');
  const format = useFormat();
  if (customization.file) {
    return <FileCustomizationChip customization={customization} file={customization.file} />;
  }
  const value = customization.valueLabel ?? String(customization.value);
  const delta = customization.priceDelta.amount;
  const label =
    delta === 0
      ? `${customization.label}: ${value}`
      : t('lines.delta', {
          label: customization.label,
          value,
          delta: `${delta > 0 ? '+' : ''}${format.money(customization.priceDelta)}`,
        });
  return (
    <Badge variant="secondary" className="font-normal">
      {label}
    </Badge>
  );
}

export interface OrderLinesTableProps {
  lines: readonly OrderLine[];
  /** Offered on lines whose answers may still be corrected; absent when the member cannot edit. */
  onEditLine?: ((line: OrderLine) => void) | undefined;
  canEditLine?: (line: OrderLine) => boolean;
}

/** The lines as sold: what, how many, at what price, with the personalisation chosen. */
export function OrderLinesTable({ lines, onEditLine, canEditLine }: OrderLinesTableProps) {
  const { t } = useTranslation('orders');
  const format = useFormat();

  return (
    <Card className="gap-0 py-0">
      <CardHeader className="border-b py-4">
        <CardTitle>{t('lines.title')}</CardTitle>
      </CardHeader>
      <CardContent className="px-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-6">{t('lines.item')}</TableHead>
              <TableHead>{t('lines.sku')}</TableHead>
              <TableHead className="text-right">{t('lines.quantity')}</TableHead>
              <TableHead className="text-right">{t('lines.unitPrice')}</TableHead>
              <TableHead className="pr-6 text-right">{t('lines.total')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.map((line) => (
              <TableRow key={line.id}>
                <TableCell className="pl-6 align-top">
                  <div className="flex items-start gap-3">
                    <LineThumb url={line.mediaUrl} title={line.title} />
                    <div className="min-w-0 space-y-1">
                      <p className="font-medium">{line.title}</p>
                      {line.variantTitle ? (
                        <p className="text-muted-foreground text-xs">{line.variantTitle}</p>
                      ) : null}
                      <p className="text-muted-foreground text-xs">
                        {t(`fulfillmentMode.${line.fulfillmentMode}`)}
                        {line.leadTimeDaysMin !== null && line.leadTimeDaysMax !== null
                          ? ` · ${t('detail.leadTime', { min: line.leadTimeDaysMin, max: line.leadTimeDaysMax })}`
                          : null}
                        {line.fulfilledQuantity > 0
                          ? ` · ${t('lines.fulfilled', { count: line.fulfilledQuantity })}`
                          : null}
                        {line.cancelledQuantity > 0
                          ? ` · ${t('lines.cancelled', { count: line.cancelledQuantity })}`
                          : null}
                      </p>
                      {line.customizations.length > 0 ? (
                        <ul
                          className="flex flex-wrap gap-1 pt-1"
                          aria-label={t('lines.customizations')}
                        >
                          {line.customizations.map((c) => (
                            <li key={c.key}>
                              <CustomizationChip customization={c} />
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      {onEditLine &&
                      line.customizations.length > 0 &&
                      (canEditLine?.(line) ?? true) ? (
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="h-auto px-0"
                          aria-label={t('lines.editLabel', { title: line.title })}
                          onClick={() => onEditLine(line)}
                        >
                          {t('lines.edit')}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="align-top">
                  <code className="text-xs">{line.sku ?? t('lines.noSku')}</code>
                </TableCell>
                <TableCell className="text-right align-top tabular-nums">{line.quantity}</TableCell>
                <TableCell className="text-right align-top tabular-nums">
                  {format.money(line.unitPrice)}
                </TableCell>
                <TableCell className="pr-6 text-right align-top font-medium tabular-nums">
                  {format.money(line.total)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
